import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar,
  Package,
  FileText,
  AlertCircle,
  LayoutList,
  LayoutGrid,
  RefreshCw,
  Loader,
  Clock
} from 'lucide-react';
import { OrderHistoryDto, OrderLineHistoryDto, OrderTextDto } from '../../../../types/orderHistory';
import orderService from '../../../../services/orderService';
import { BackButton } from '../../../common/BackButton';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { OrderTimelineModal } from '../order-timeline';

// Import the reusable warehouse table system
import { WarehouseTable, createColumnFactory } from '../../../shared/table';
import type { ColumnDefinition, ExpandableRowConfig, TableDataRow } from '../../../shared/table';

// Extended type for order history table
interface OrderHistoryTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderStatus: number;
  statusText: string;
  orderDate: string | null;
  deliveryDate: string | null;
  customerNo: string;
  customerName: string | null;
  // Leveringsadresse
  delCustomerName: string | null;
  delAddress1: string | null;
  delCity: string | null;
  delZipCode: string | null;
  // Referanser
  customerOrderNo: string | null;
  // Detaljer
  orderTotal: number | null;
  carrierName: string | null;
  warehouse: string | null;
  pListID: number | null;
  operatorId: string | null;
  operatorName: string | null;
  totalLines: number;
  totalQty: number;
  // Lines and texts
  lines: OrderLineHistoryDto[];
  orderTexts: OrderTextDto[];
  // Original
  _original: OrderHistoryDto;
}

// Extended type for flat order line table
interface OrderLineTableRow extends TableDataRow {
  id: number;
  orderNo: number;
  orderNoERP: string;
  orderLineNo: number;
  lineStatus: number;
  statusText: string;
  itemNo: string;
  itemName: string | null;
  qtyOrdered: number;
  qtyPicked: number;
  qtyDelivered: number;
  unit: string | null;
  batchID: string | null;
  locationNo: string | null;
  operatorId: string | null;
  operatorName: string | null;
  pListID: number | null;
  customerNo: string | null;
  customerName: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  _original: OrderLineHistoryDto;
}

// Create column factories
const orderColumnFactory = createColumnFactory<OrderHistoryTableRow>();
const lineColumnFactory = createColumnFactory<OrderLineTableRow>();

interface OrderHistoryProps {
  onBack?: () => void;
}

type ViewMode = 'master-detail' | 'flat';

export const OrderHistory: React.FC<OrderHistoryProps> = ({ onBack }) => {
  // State management
  const { currentWarehouse } = useWarehouse();
  const [viewMode, setViewMode] = useState<ViewMode>('master-detail');
  const [orders, setOrders] = useState<OrderHistoryDto[]>([]);
  const [flatLines, setFlatLines] = useState<OrderLineHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timeline modal state
  const [timelineOrderNo, setTimelineOrderNo] = useState<number | null>(null);

  // Date filtering - use YYYY-MM-DD strings to avoid timezone issues
  const formatDateStr = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [fromDate, setFromDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default 7 days ago
    return formatDateStr(date);
  });
  const [toDate, setToDate] = useState<string>(formatDateStr(new Date()));
  const [dateError, setDateError] = useState<string | null>(null);

  // Helper: check if YYYY-MM-DD string is a valid date
  const isValidDateStr = (str: string): boolean => {
    return /^\d{4}-\d{2}-\d{2}$/.test(str);
  };

  // Validate date range - returns error message or null
  const validateDateRange = useCallback((from: string, to: string): string | null => {
    if (!isValidDateStr(from) || !isValidDateStr(to)) {
      return 'Ugyldig dato';
    }
    if (from > to) {
      return 'Fra-dato må være før til-dato';
    }
    const fromMs = new Date(from + 'T00:00:00').getTime();
    const toMs = new Date(to + 'T00:00:00').getTime();
    const daysDiff = Math.ceil((toMs - fromMs) / (1000 * 60 * 60 * 24));
    if (daysDiff > 30) {
      return 'Maksimalt 30 dagers periode tillatt';
    }
    return null;
  }, []);

  // Update dateError for UI display
  useEffect(() => {
    setDateError(validateDateRange(fromDate, toDate));
  }, [fromDate, toDate, validateDateRange]);

  // Fetch data - triggered by Oppdater button, not automatically on date change
  const fetchData = useCallback(async () => {
    const validationError = validateDateRange(fromDate, toDate);
    if (validationError) return;

    setLoading(true);
    setError(null);

    try {
      if (viewMode === 'master-detail') {
        const data = await orderService.getOrdersHistory(fromDate, toDate, currentWarehouse || undefined);
        setOrders(data);
      } else {
        const data = await orderService.getOrderLinesHistoryFlat(fromDate, toDate, currentWarehouse || undefined);
        setFlatLines(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  }, [viewMode, fromDate, toDate, currentWarehouse, validateDateRange]);

  // Only auto-fetch on initial mount
  const hasLoadedRef = React.useRef(false);
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchData();
    }
  }, [fetchData]);

  // Transform orders to table data
  const orderTableData: OrderHistoryTableRow[] = useMemo(() => {
    return orders.map(order => ({
      id: order.orderNo,
      orderNo: order.orderNo,
      orderNoERP: order.orderNoERP || order.orderNo.toString(),
      orderStatus: order.orderStatus,
      statusText: order.statusText,
      orderDate: order.orderDate || null,
      deliveryDate: order.deliveryDate || null,
      customerNo: order.customerNo,
      customerName: order.customerName || null,
      delCustomerName: order.delCustomerName || null,
      delAddress1: order.delAddress1 || null,
      delCity: order.delCity || null,
      delZipCode: order.delZipCode || null,
      customerOrderNo: order.customerOrderNo || null,
      orderTotal: order.orderTotal ?? null,
      carrierName: order.carrierName || null,
      warehouse: order.warehouse || null,
      pListID: order.pListID ?? null,
      operatorId: order.operatorId || null,
      operatorName: order.operatorName || null,
      totalLines: order.totalLines,
      totalQty: order.totalQty,
      lines: order.lines || [],
      orderTexts: order.orderTexts || [],
      _original: order
    }));
  }, [orders]);

  // Transform flat lines to table data
  const lineTableData: OrderLineTableRow[] = useMemo(() => {
    return flatLines.map((line, idx) => ({
      id: idx,
      orderNo: line.orderNo,
      orderNoERP: line.orderNoERP || line.orderNo.toString(),
      orderLineNo: line.orderLineNo,
      lineStatus: line.lineStatus,
      statusText: line.statusText,
      itemNo: line.itemNo,
      itemName: line.itemName || null,
      qtyOrdered: line.qtyOrdered,
      qtyPicked: line.qtyPicked,
      qtyDelivered: line.qtyDelivered,
      unit: line.unit || null,
      batchID: line.batchID || null,
      locationNo: line.locationNo || null,
      operatorId: line.operatorId || null,
      operatorName: line.operatorName || null,
      pListID: line.pListID ?? null,
      customerNo: line.customerNo || null,
      customerName: line.customerName || null,
      orderDate: line.orderDate || null,
      deliveryDate: line.deliveryDate || null,
      _original: line
    }));
  }, [flatLines]);

  // Row ID functions
  const getOrderRowId = useCallback((row: OrderHistoryTableRow) => row.orderNo, []);
  const getLineRowId = useCallback((row: OrderLineTableRow) => row.id, []);

  // Status color helper
  const getStatusColor = (status: number): string => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';
      case 10: return 'bg-yellow-100 text-yellow-800';
      case 20: return 'bg-blue-100 text-blue-800';
      case 30: return 'bg-orange-100 text-orange-800';
      case 40: return 'bg-green-100 text-green-800';
      case 50: return 'bg-indigo-100 text-indigo-800';
      case 60: return 'bg-emerald-100 text-emerald-800';
      case 70: return 'bg-purple-100 text-purple-800';
      case 80: return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Order columns based on legacy OrdersHistory.dfm
  const orderColumns: ColumnDefinition<OrderHistoryTableRow>[] = useMemo(() => [
    // === TIDSLINJE-KNAPP ===
    orderColumnFactory.createTextColumn('_timeline' as any, '_timeline' as any, '', {
      width: 44,
      sortable: false,
      renderer: (_value, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setTimelineOrderNo(row.orderNo); }}
          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
          title="Vis ordretidslinje"
        >
          <Clock size={18} />
        </button>
      )
    }),
    // === HOVEDFELTER (synlige) ===
    orderColumnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'Ordrenr.', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createTextColumn('statusText', 'statusText', 'Ordrestatus', {
      width: 120,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.orderStatus)}`}>
          {value}
        </span>
      )
    }),
    orderColumnFactory.createTextColumn('customerNo', 'customerNo', 'Kundenr', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createTextColumn('customerName', 'customerName', 'Kundenavn', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createDateColumn('orderDate', 'orderDate', 'Ordredato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 110,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createNumberColumn('pListID', 'pListID', 'Plukklistenr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    orderColumnFactory.createTextColumn('warehouse', 'warehouse', 'Lagersted', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createTextColumn('carrierName', 'carrierName', 'Transportør', {
      width: 140,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    orderColumnFactory.createNumberColumn('totalLines', 'totalLines', 'Ant. linjer', {
      width: 90,
      sortable: true,
      filterType: 'both'
    }),
    orderColumnFactory.createNumberColumn('totalQty', 'totalQty', 'Totalt ant.', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(0) : '-'
    }),

    // === LEVERINGSADRESSE (skjult) ===
    orderColumnFactory.createTextColumn('delCustomerName', 'delCustomerName', 'Lev. kundenavn', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delAddress1', 'delAddress1', 'Lev. adr. 1', {
      width: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delZipCode', 'delZipCode', 'Lev. postnr', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    orderColumnFactory.createTextColumn('delCity', 'delCity', 'Lev. poststed', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === REFERANSER (skjult) ===
    orderColumnFactory.createTextColumn('customerOrderNo', 'customerOrderNo', 'Oppr. bestnr', {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === OPERATØR (skjult) ===
    orderColumnFactory.createTextColumn('operatorName', 'operatorName', 'Operatør', {
      width: 120,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),

    // === ORDRESUM (skjult) ===
    orderColumnFactory.createNumberColumn('orderTotal', 'orderTotal', 'Ordresum', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toFixed(2) : '-',
      visible: false
    })
  ], []);

  // Flat line columns
  const lineColumns: ColumnDefinition<OrderLineTableRow>[] = useMemo(() => [
    lineColumnFactory.createTextColumn('orderNoERP', 'orderNoERP', 'Ordrenr.', {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createNumberColumn('orderLineNo', 'orderLineNo', 'Linjenr', {
      width: 70,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createTextColumn('customerName', 'customerName', 'Kunde', {
      width: 150,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('itemNo', 'itemNo', 'Varenr.', {
      width: 120,
      sortable: true,
      filterType: 'both'
    }),
    lineColumnFactory.createTextColumn('itemName', 'itemName', 'Varebeskrivelse', {
      width: 200,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createNumberColumn('qtyOrdered', 'qtyOrdered', 'Ant. best.', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createNumberColumn('qtyPicked', 'qtyPicked', 'Ant. plukket', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createNumberColumn('qtyDelivered', 'qtyDelivered', 'Ant. levert', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== undefined && value !== null ? value.toFixed(2) : '-'
    }),
    lineColumnFactory.createTextColumn('statusText', 'statusText', 'Status', {
      width: 100,
      sortable: true,
      filterType: 'dropdown',
      renderer: (value, row) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.lineStatus)}`}>
          {value}
        </span>
      )
    }),
    lineColumnFactory.createTextColumn('unit', 'unit', 'Enhet', {
      width: 70,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createNumberColumn('pListID', 'pListID', 'Plukkliste', {
      width: 90,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value !== null ? value.toString() : '-'
    }),
    lineColumnFactory.createTextColumn('locationNo', 'locationNo', 'Lokasjon', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('operatorName', 'operatorName', 'Operatør', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-'
    }),
    lineColumnFactory.createTextColumn('batchID', 'batchID', 'Batchnr.', {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
      visible: false
    }),
    lineColumnFactory.createDateColumn('orderDate', 'orderDate', 'Ordredato', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    }),
    lineColumnFactory.createDateColumn('deliveryDate', 'deliveryDate', 'Leveringsdato', {
      width: 100,
      sortable: true,
      filterType: 'both',
      visible: false
    })
  ], []);

  // Render order lines detail
  const renderOrderLines = useCallback((order: OrderHistoryTableRow) => {
    const lines = order.lines;
    const texts = order.orderTexts;

    return (
      <div className="bg-slate-50 p-3 border-t border-slate-200">
        {/* Order Lines */}
        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
          <Package size={16} />
          <span>Ordrelinjer ({lines.length})</span>
        </h4>

        {lines.length > 0 ? (
          <table className="w-full text-xs mb-4">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-100">
                <th className="text-left py-2 px-3 font-medium text-slate-600">Linjenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Varenr</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Varebeskrivelse</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Bestilt</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Plukket</th>
                <th className="text-right py-2 px-3 font-medium text-slate-600">Levert</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Enhet</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Lokasjon</th>
                <th className="text-left py-2 px-3 font-medium text-slate-600">Operatør</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {lines.map((line) => (
                <tr key={line.orderLineNo} className="hover:bg-slate-100 transition-colors">
                  <td className="py-2 px-3 text-slate-900">{line.orderLineNo}</td>
                  <td className="py-2 px-3 text-slate-900 font-medium font-mono">{line.itemNo}</td>
                  <td className="py-2 px-3 text-slate-900">{line.itemName || '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyOrdered?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right font-medium">{line.qtyPicked?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900 text-right">{line.qtyDelivered?.toFixed(2) ?? '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.unit || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.locationNo || '-'}</td>
                  <td className="py-2 px-3 text-slate-900">{line.operatorName || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-4 text-gray-500 text-sm mb-4">
            Ingen ordrelinjer
          </div>
        )}

        {/* Order Texts */}
        {texts.length > 0 && (
          <>
            <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
              <FileText size={16} />
              <span>Ordretekster ({texts.length})</span>
            </h4>
            <div className="bg-white rounded border border-slate-200 p-3 max-h-32 overflow-y-auto">
              {texts.map((text, idx) => (
                <div key={`${text.textNo}-${text.textLine}`} className="text-xs text-slate-700 mb-1">
                  <span className="font-medium text-slate-500 mr-2">
                    [{text.textNo}.{text.textLine}]
                  </span>
                  {text.text}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }, []);

  // Expandable configuration for orders
  const expandableConfig: ExpandableRowConfig<OrderHistoryTableRow, null> = useMemo(() => ({
    getDetailData: () => null,
    renderDetail: renderOrderLines,
    expandOnRowClick: false
  }), [renderOrderLines]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: 'Oppdater',
      icon: RefreshCw,
      onClick: fetchData,
      variant: 'secondary' as const
    }
  ], [fetchData]);

  // Date filter toolbar component
  const dateFilterToolbar = useMemo(() => (
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Calendar size={18} className="text-blue-500" />
        <label className="text-sm font-medium text-gray-700">Fra:</label>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            if (e.target.value) {
              setFromDate(e.target.value);
            }
          }}
          className="px-3 py-1.5 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700">Til:</label>
        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            if (e.target.value) {
              setToDate(e.target.value);
            }
          }}
          className="px-3 py-1.5 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
        />
      </div>
      {dateError && (
        <div className="flex items-center text-red-600 text-sm">
          <AlertCircle size={16} className="mr-1" />
          {dateError}
        </div>
      )}
      <div className="text-sm text-gray-600 font-medium">
        {viewMode === 'master-detail'
          ? `${orderTableData.length} ordre`
          : `${lineTableData.length} ordrelinjer`}
      </div>
    </div>
  ), [fromDate, toDate, dateError, viewMode, orderTableData.length, lineTableData.length]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-4 pb-2 bg-white border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <BackButton onClick={onBack || (() => {})} />
            <h1 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
              <Package size={24} />
              <span>Ordrehistorikk</span>
            </h1>
          </div>
          <div className="flex items-center space-x-2">
            {/* View mode toggle */}
            <button
              onClick={() => setViewMode('master-detail')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                viewMode === 'master-detail'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutGrid className="mr-2" size={18} />
              Ordre
            </button>
            <button
              onClick={() => setViewMode('flat')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center ${
                viewMode === 'flat'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutList className="mr-2" size={18} />
              Ordrelinjer
            </button>
          </div>
        </div>
      </div>

      {/* Table content */}
      <div className="flex-1 min-h-0 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <Loader className="animate-spin" size={20} />
              <span>Laster ordrehistorikk...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
            <h3 className="text-red-800 font-medium flex items-center">
              <AlertCircle size={20} className="mr-2" />
              Feil ved lasting av ordrehistorikk
            </h3>
            <p className="text-red-600 text-sm mt-1">{error}</p>
            <button
              onClick={fetchData}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 flex items-center space-x-2"
            >
              <RefreshCw size={16} />
              <span>Prøv igjen</span>
            </button>
          </div>
        ) : viewMode === 'master-detail' ? (
          <WarehouseTable
            data={orderTableData}
            columns={orderColumns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'order-history-table'
            }}
            getRowId={getOrderRowId}
            tableActions={tableActions}
            expandableConfig={expandableConfig}
            toolbarLeft={dateFilterToolbar}
            loading={false}
            error={undefined}
            aria-label="Ordrehistorikk tabell"
          />
        ) : (
          <WarehouseTable
            data={lineTableData}
            columns={lineColumns}
            config={{
              showPagination: true,
              showColumnConfig: true,
              showGlobalSearch: true,
              showFilters: true,
              showExport: false,
              showSummary: false,
              defaultPageSize: 50,
              allowColumnReorder: true,
              allowColumnResize: true,
              stickyHeader: true,
              compactMode: true,
              persistenceKey: 'order-history-flat-table'
            }}
            getRowId={getLineRowId}
            tableActions={tableActions}
            toolbarLeft={dateFilterToolbar}
            loading={false}
            error={undefined}
            aria-label="Ordrelinjer historikk tabell"
          />
        )}
      </div>

      {/* Order Timeline Modal */}
      {timelineOrderNo !== null && (
        <OrderTimelineModal
          isOpen={true}
          orderNo={timelineOrderNo}
          onClose={() => setTimelineOrderNo(null)}
        />
      )}
    </div>
  );
};

export default OrderHistory;
